import { useEffect, useState } from "react";
import { downloadReport } from "../../../../utils/api";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";
import { Buffer } from "buffer";
import { DocumentDownloadIcon, DocumentReportIcon } from "@heroicons/react/solid";

import { REPORT_CATEGORIES, NO_ACCESS_TYPES, PERMISSIONS } from "../../../../utils/constants";
import diversityIllustration from "../../../../assets/images/diversity.svg";

import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import AccessControl from "../../../SharedComponents/AccessControl";
import SectionPlaceholder from "../../../SharedComponents/Loading/SectionPlaceholder";
import CareeringAhead from "./ReportTypes/CareeringAhead/CareeringAhead";
import SubjectiveCognitiveDecline from "./ReportTypes/SubjectiveCognitiveDecline/SubjectiveCognitiveDecline";

const getCategoryData = (category, i, field) => {
    const categories = REPORT_CATEGORIES;
    return categories[category] ? categories[category][field] : Object.values(categories)[i][field];
};

function Report() {
    const [score, setScore] = useState(null);
    const [isNewFormat, setIsNewFormat] = useState(false);
    const [categories, setCategories] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showMore, setShowMore] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    const getBandDescription = (percentileNumber, name) => {
        if (percentileNumber <= 33) {
            return "The individual is low in " + name;
        } else if (percentileNumber < 74) {
            return "The individual is average in " + name;
        } else {
            return "The individual is high in " + name;
        }
    };

    const handleShowMore = () => {
        setShowMore((expand) => !expand);
    };

    useEffect(() => {
        const fetchReportContent = async () => {
            setLoading(true);

            try {
                const documentResponse = await downloadReport(location.state.reportIdentifier);

                const score = JSON.parse(Buffer.from(documentResponse.content, "base64").toString());

                let categories = [];
                let newFormat = false;

                if (score[0] && score[0].category) {
                    newFormat = false;
                    setIsNewFormat(false);

                    categories = score.reduce(
                        (prev, curr) => (!prev.includes(curr.category) ? [...prev, curr.category] : prev),
                        []
                    );
                } else if (score.reportFields[0] && score.reportFields[0].category) {
                    newFormat = true;
                    setIsNewFormat(true);

                    const scoreReportFields = [...score.reportFields];
                    categories = scoreReportFields.reduce(
                        (prev, curr) => (!prev.includes(curr.category) ? [...prev, curr.category] : prev),
                        []
                    );
                }

                if (newFormat === true) {
                    score.reportFields.map((field, index) => {
                        field.categoryIndex = categories.indexOf(field.category);
                        score.reportFields
                            .filter((score) => score.category === field.category)
                            .map((score, index) => (score.fieldIndex = index));

                        return score;
                    });
                } else {
                    score.map((field) => {
                        field.categoryIndex = categories.indexOf(field.category);
                        score
                            .filter((score) => score.category === field.category)
                            .map((score, index) => (score.fieldIndex = index));

                        return score;
                    });
                }

                setCategories(categories);
                setScore(score);
            } catch {
                setCategories(null);
                setScore(null);
            }

            setLoading(false);
        };

        if (location.state?.reportIdentifier) {
            fetchReportContent();
        } else {
            navigate("/reports");
        }
    }, [location.state?.reportIdentifier, navigate]);

    return (
        <AccessControl requiredPermission={PERMISSIONS.VIEW_REPORTS} noAccessType={NO_ACCESS_TYPES.REDIRECT}>
            <DashboardContainer
                title={"Report"}
                titleIcon={<DocumentReportIcon className="block h-5 w-5 mr-2" />}
                actionButton
                actionButtonText={"Download report"}
                actionButtonIcon={<DocumentDownloadIcon className="block h-4 w-4 mr-2" />}
                actionButtonPermission={PERMISSIONS.DOWNLOAD_REPORTS}
            >
                {loading ? (
                    <SectionPlaceholder />
                ) : (
                    <>
                        <div className="flex flex-col gap-6 bg-blue-gray-50 rounded-xl p-5 shadow-sm">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="md:col-span-2 flex flex-col gap-4">
                                    <div className={"z-30"}>
                                        <h2 className={"text-primary-700 text-xl font-semibold"}>
                                            Report for{" "}
                                            <span className={"text-primary-500 font-medium"}>
                                                {location.state?.userName}
                                            </span>
                                        </h2>
                                        <div className={"text-blue-gray-400 text-md mt-1"}>Prepared by Psych Press</div>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                                <div className="md:col-span-2 flex flex-col gap-4">
                                    {score?.reportName === "NAPP - Subjective Cognitive Decline (SCD)" ? (
                                        <SubjectiveCognitiveDecline score={score} userName={location.state?.userName} />
                                    ) : (
                                        <CareeringAhead
                                            score={score}
                                            categories={categories}
                                            getCategoryData={getCategoryData}
                                            getBandDescription={getBandDescription}
                                            isNewFormat={isNewFormat}
                                            userName={location.state.userName}
                                            showMore={showMore}
                                            handleShowMor={handleShowMore}
                                        />
                                    )}
                                </div>
                                <div className="flex flex-col gap-4 md:col-span-1 mt-11">
                                    <div className="bg-blue-50 p-4 rounded-xl shadow-sm">
                                        <div className="space-y-3">
                                            <p className="text-lg font-semibold text-blue-500">
                                                Have a question about this report?
                                            </p>
                                            <p className="text-blue-gray-500">
                                                Contact us at{" "}
                                                <span className="text-blue-500">techsupport@psychpress.com.au</span>
                                            </p>
                                        </div>
                                    </div>
                                    <div className="bg-amber-50 p-4 rounded-xl shadow-sm">
                                        <div className="space-y-3">
                                            <p className="text-lg font-semibold text-amber-500">Confidentiality</p>
                                            <div className="text-blue-gray-500 grid grid-flow-row gap-3">
                                                <p>
                                                    This highly confidential document is provided to the client on the
                                                    candidate named on the cover sheet on the basis that the need for
                                                    this confidentiality is recognised, accepted and that such
                                                    confidentiality will be strictly maintained.
                                                </p>
                                                <p>
                                                    It should therefore only be read by staff specifically involved with
                                                    the selection, promotion or development of the person named, and
                                                    stored securely with minimum access.
                                                </p>
                                                <p>
                                                    Should a report be required at a later date, it can be obtained
                                                    without further cost, from Psych Press archives.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    {categories && (
                                        <div className="bg-white p-4 rounded-xl shadow-sm hidden md:flex">
                                            <div className="space-y-3">
                                                <p className="text-lg font-semibold text-blue-700">Legend</p>
                                                <div className="grid grid-cols-2 gap-3">
                                                    {categories.map((category, index) => (
                                                        <div
                                                            className="flex flex-row items-center space-x-2"
                                                            key={index}
                                                        >
                                                            {getCategoryData(category, index, "icon")}
                                                            <p
                                                                className={
                                                                    "text-md font-medium " +
                                                                    getCategoryData(category, index, "text")
                                                                }
                                                            >
                                                                {category}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="bg-white p-4 rounded-xl shadow-sm">
                                        <div className="space-y-3">
                                            <p className="text-lg font-semibold text-blue-700">Objectives</p>
                                            <p className="text-blue-gray-500">
                                                The report on the candidate's capabilities has been based on an
                                                assessment used to provide objective information about the competencies.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="bg-white p-4 rounded-xl shadow-sm">
                                        <div className="space-y-3">
                                            <p className="text-lg font-semibold text-blue-700">
                                                Cross validation of outcomes
                                            </p>
                                            <p className="text-blue-gray-500">
                                                This report provides objective information on the candidate's
                                                capabilities. We recommend supplementing it with other information
                                                obtained from other sources such as interviews or other reports.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="bg-white p-4 rounded-xl shadow-sm">
                                        <div className="space-y-3">
                                            <p className="text-lg font-semibold text-blue-700">
                                                Cultural considerations
                                            </p>

                                            <div className="flex justify-center min-w-full bg-transparent">
                                                <img
                                                    alt="Psych Press Portal Logo"
                                                    className="w-80"
                                                    src={diversityIllustration}
                                                />
                                            </div>

                                            <div className="text-blue-gray-500 grid grid-flow-row gap-3">
                                                <p>
                                                    It is important to take into consideration any cultural impacts when
                                                    interpreting the reports. Psych Press tries to ensure all
                                                    assessments are culture fair and norm comparisons are done
                                                    comparative to appropriate cultural groups or affiliations. However,
                                                    when reviewing the report, it is always important to review using
                                                    the context of the cultural lens. Please contact one of our Psych
                                                    Press consultants to help you provide more details for your report
                                                    and the impact of culture on assessment results and subsequent
                                                    interpretation.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </DashboardContainer>
        </AccessControl>
    );
}

export default Report;
